/**
 * ConfigVentasPage.jsx
 * Exporta componentes reutilizables para el tab de configuración dentro de VentasPage.
 * No se usa como página standalone — el tab vive directamente en VentasPage.
 */
import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  X, Search, Trash2, Edit2, Users, Info, AlertTriangle, Percent, Plus, Loader2, Ban, CheckCircle2
} from "lucide-react";
import { AuthContext } from "../App";
import { toast } from "sonner";
import ComisionesConfigPanel from "../components/ComisionesConfigPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MODO_LABELS = {
  del_producto: {
    label: "Del producto",
    desc: "Hasta el % máximo configurado en cada producto",
    badge: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  },
  sin_limite: {
    label: "Sin límite",
    desc: "Puede aplicar cualquier % sin tope personal",
    badge: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  },
  personalizado: {
    label: "Personalizado",
    desc: "Tope personal + excepciones por producto",
    badge: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  },
};

// ── Modal de edición de config por usuario ────────────────────────────────────
export function DescuentoConfigModal({ user: targetUser, onClose, onSaved, hdrs, logoTipo }) {
  const [modo, setModo] = useState(null);
  const [limite, setLimite] = useState("10");
  const [excepciones, setExcepciones] = useState([]);
  const [searchProd, setSearchProd] = useState("");
  const [productos, setProductos] = useState([]);
  const [loadingProds, setLoadingProds] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const cfg = targetUser?.descuento_config;
    if (cfg) {
      setModo(cfg.modo || null);
      setLimite(String(cfg.limite ?? 10));
      setExcepciones(cfg.excepciones || []);
    } else {
      setModo(null); setLimite("10"); setExcepciones([]);
    }
  }, [targetUser]);

  const buscarProductos = useCallback(async (q) => {
    if (!q || q.length < 2) { setProductos([]); return; }
    setLoadingProds(true);
    try {
      const r = await fetch(
        `${API}/admin/productos?search=${encodeURIComponent(q)}${logoTipo ? `&logo_tipo=${logoTipo}` : ""}`,
        { headers: hdrs }
      );
      const d = await r.json();
      setProductos((d.productos || []).filter(p => p.descuento_maximo > 0 && p.activo));
    } catch { setProductos([]); }
    setLoadingProds(false);
  }, [hdrs, logoTipo]);

  useEffect(() => {
    const t = setTimeout(() => buscarProductos(searchProd), 300);
    return () => clearTimeout(t);
  }, [searchProd, buscarProductos]);

  const agregarExcepcion = (prod) => {
    if (excepciones.some(e => e.producto_id === prod.id)) return;
    setExcepciones(prev => [...prev, { producto_id: prod.id, nombre: prod.nombre, sku: prod.sku }]);
    setSearchProd(""); setProductos([]);
  };

  const quitarExcepcion = (pid) => setExcepciones(prev => prev.filter(e => e.producto_id !== pid));

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = modo
        ? { modo, ...(modo === "personalizado" ? { limite: parseFloat(limite) || 0, excepciones } : {}) }
        : { modo: null };
      const r = await fetch(`${API}/admin/usuarios/${targetUser.id}/descuento-config`, {
        method: "PUT",
        headers: { ...hdrs, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error al guardar");
      toast.success("Configuración guardada");
      onSaved(targetUser.id, d.descuento_config);
      onClose();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <p className="text-white font-semibold">{targetUser?.name || targetUser?.email}</p>
            <p className="text-slate-500 text-xs">{targetUser?.email}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Modo */}
          <div>
            <p className="text-slate-400 text-xs font-semibold mb-2">Modo de descuento</p>
            <div className="space-y-2">
              {/* Sin descuento */}
              <button type="button" onClick={() => setModo(null)}
                className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${!modo ? "border-slate-500 bg-slate-500/10" : "border-white/10 bg-white/3 hover:border-white/20"}`}>
                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 flex items-center justify-center ${!modo ? "border-slate-400 bg-slate-400" : "border-slate-600"}`}>
                  {!modo && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Sin descuentos</p>
                  <p className="text-slate-500 text-xs">El campo no aparece para este usuario</p>
                </div>
              </button>
              {Object.entries(MODO_LABELS).map(([key, info]) => (
                <button key={key} type="button" onClick={() => setModo(key)}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                    modo === key
                      ? key === "del_producto" ? "border-cyan-500/50 bg-cyan-500/5"
                        : key === "sin_limite"  ? "border-amber-500/50 bg-amber-500/5"
                        : "border-purple-500/50 bg-purple-500/5"
                      : "border-white/10 bg-white/3 hover:border-white/20"
                  }`}>
                  <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 flex items-center justify-center ${
                    modo === key
                      ? key === "del_producto" ? "border-cyan-400 text-cyan-400"
                        : key === "sin_limite"  ? "border-amber-400 text-amber-400"
                        : "border-purple-400 text-purple-400"
                      : "border-slate-600"
                  }`}>
                    {modo === key && <div className="w-1.5 h-1.5 rounded-full bg-current" />}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${
                      modo === key
                        ? key === "del_producto" ? "text-cyan-400"
                          : key === "sin_limite"  ? "text-amber-400"
                          : "text-purple-400"
                        : "text-white"
                    }`}>{info.label}</p>
                    <p className="text-slate-500 text-xs">{info.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Personalizado: límite + excepciones */}
          {modo === "personalizado" && (
            <div className="space-y-4">
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Límite personal (%)</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="100" step="0.5"
                    value={limite} onChange={e => setLimite(e.target.value)}
                    className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500" />
                  <span className="text-slate-400 text-sm">% máximo en cualquier producto</span>
                </div>
              </div>

              <div>
                <p className="text-slate-400 text-xs font-semibold mb-1">Excepciones por producto</p>
                <p className="text-slate-500 text-[11px] mb-2">
                  Estos productos permiten llegar hasta el descuento máximo del propio producto.
                </p>
                {excepciones.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {excepciones.map(e => (
                      <div key={e.producto_id} className="flex items-center justify-between bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-1.5">
                        <div>
                          <span className="text-white text-xs font-medium">{e.nombre}</span>
                          {e.sku && <span className="text-slate-500 text-[10px] ml-1.5">#{e.sku}</span>}
                        </div>
                        <button onClick={() => quitarExcepcion(e.producto_id)} className="text-slate-500 hover:text-red-400 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                  <input type="text" placeholder="Buscar producto con descuento habilitado..."
                    value={searchProd} onChange={e => setSearchProd(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white text-xs focus:outline-none focus:border-purple-500" />
                </div>
                {loadingProds && <p className="text-slate-500 text-xs mt-1">Buscando...</p>}
                {productos.length > 0 && (
                  <div className="mt-1 border border-white/10 rounded-lg overflow-hidden bg-slate-800 max-h-40 overflow-y-auto">
                    {productos.map(p => (
                      <button key={p.id} type="button" onClick={() => agregarExcepcion(p)}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-0 transition-all">
                        <div className="text-left">
                          <span className="text-white text-xs font-medium">{p.nombre}</span>
                          {p.sku && <span className="text-slate-500 text-[10px] ml-1.5">#{p.sku}</span>}
                        </div>
                        <span className="text-amber-400 text-xs font-mono ml-2">max {p.descuento_maximo}%</span>
                      </button>
                    ))}
                  </div>
                )}
                {searchProd.length >= 2 && !loadingProds && productos.length === 0 && (
                  <p className="text-slate-500 text-xs mt-1">Sin resultados (solo se muestran productos con descuento habilitado)</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-5 pt-0">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-slate-400 hover:text-white text-sm transition-all">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-all disabled:opacity-50">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab embebible en VentasPage ───────────────────────────────────────────────
export function DescuentosConfigTab() {
  const { user, activeEmpresaPropia, token } = useContext(AuthContext);
  const hdrs = token ? { Authorization: `Bearer ${token}` } : {};
  const logoTipo = activeEmpresaPropia?.slug;

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const cargarUsuarios = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const hdrs = { Authorization: `Bearer ${token}` };
      const params = logoTipo ? `?logo_tipo=${logoTipo}` : "";
      const r = await fetch(`${API}/admin/ventas/descuentos-usuarios${params}`, { headers: hdrs });
      if (!r.ok) {
        const err = await r.text();
        console.error("descuentos-usuarios error:", r.status, err);
        toast.error(`Error ${r.status} al cargar usuarios`);
        setLoading(false);
        return;
      }
      const d = await r.json();
      console.log("descuentos-usuarios response:", d);
      setUsuarios(Array.isArray(d) ? d : (d.usuarios || []));
    } catch (e) { console.error("descuentos-usuarios fetch error:", e); toast.error("Error al cargar usuarios"); }
    setLoading(false);
  }, [token, logoTipo]);

  useEffect(() => { cargarUsuarios(); }, [cargarUsuarios]);

  const handleSaved = (userId, newCfg) => {
    setUsuarios(prev => prev.map(u => u.id === userId ? { ...u, descuento_config: newCfg } : u));
  };

  const modoInfo = (cfg) => {
    if (!cfg?.modo) return { label: "Sin descuentos", badge: "bg-slate-500/20 text-slate-400 border-slate-500/30" };
    return MODO_LABELS[cfg.modo] || { label: cfg.modo, badge: "bg-white/10 text-white border-white/20" };
  };

  return (
    <div className="space-y-4 py-4">
      {/* Info card */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3">
        <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-300 space-y-1">
          <p><strong className="text-white">Admin / Gerente / Super Admin</strong> — Bypass total: pueden aplicar cualquier descuento siempre.</p>
          <p><strong className="text-cyan-300">Del producto</strong> — Hasta el % máximo configurado en el producto.</p>
          <p><strong className="text-amber-300">Sin límite</strong> — Sin tope personal.</p>
          <p><strong className="text-purple-300">Personalizado</strong> — Tope propio con excepciones por producto.</p>
          <p className="text-slate-500">El descuento máximo por producto se configura desde Catálogo Productos.</p>
        </div>
      </div>

      {/* Tabla usuarios */}
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-white">Usuarios</span>
            <span className="text-xs text-slate-500">({usuarios.length})</span>
          </div>
          <button onClick={cargarUsuarios} className="text-slate-500 hover:text-white text-xs transition-colors">
            Actualizar
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm">Cargando...</div>
        ) : usuarios.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm">Sin usuarios encontrados</div>
        ) : (
          <div className="divide-y divide-white/5">
            {usuarios.map(u => {
              const info = modoInfo(u.descuento_config);
              return (
                <div key={u.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-white/3 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-300 text-sm font-bold">
                      {(u.name || u.email || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{u.name || u.email}</p>
                      <p className="text-slate-500 text-xs">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${info.badge}`}>
                        {info.label}
                      </span>
                      {u.descuento_config?.modo === "personalizado" && (
                        <p className="text-slate-500 text-[10px] mt-0.5 text-right">
                          Límite: {u.descuento_config.limite}%
                          {(u.descuento_config.excepciones || []).length > 0 &&
                            ` · ${u.descuento_config.excepciones.length} excepción${u.descuento_config.excepciones.length !== 1 ? "es" : ""}`}
                        </p>
                      )}
                    </div>
                    <button onClick={() => setEditTarget(u)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editTarget && (
        <DescuentoConfigModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
          hdrs={hdrs}
          logoTipo={logoTipo}
        />
      )}
    </div>
  );
}

// ── Vendedores y Comisiones — Tab embebible en VentasPage ─────────────────────
// El vendedor de una venta ES el usuario que la carga (automático, ya no se
// elige de una lista con nombre libre). Acá solo se configura el % de
// comisión de cada usuario de esta empresa — la lista de usuarios en sí se
// gestiona en Usuarios (alta/baja/permisos).
export function VendedoresConfigTab() {
  const { activeEmpresaPropia, token } = useContext(AuthContext);
  const logoTipo = activeEmpresaPropia?.slug;

  const [usuarios, setUsuarios] = useState([]);
  const [vendedoresEnt, setVendedoresEnt] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [pctEdit, setPctEdit] = useState("");
  const [saving, setSaving] = useState(false);

  const hdrs = token ? { Authorization: `Bearer ${token}` } : {};

  const cargar = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (logoTipo) params.set("logo_tipo", logoTipo);
      const r = await fetch(`${API}/admin/vendedores/usuarios?${params.toString()}`, { headers: hdrs });
      const d = await r.json();
      setUsuarios(Array.isArray(d) ? d : []);
      const rv = await fetch(`${API}/admin/vendedores/entidades?${params.toString()}`, { headers: hdrs });
      if (rv.ok) { const dv = await rv.json(); setVendedoresEnt(Array.isArray(dv) ? dv : []); }
    } catch (e) { toast.error("Error al cargar usuarios"); }
    setLoading(false);
  }, [token, logoTipo]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirEditar = (u) => {
    setEditandoId(u.id);
    setPctEdit(String(u.porcentaje_comision ?? "0"));
  };

  const guardar = async (userId) => {
    const pct = parseFloat(pctEdit);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("El % de comisión debe estar entre 0 y 100"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/vendedores/usuarios/${userId}/comision`, {
        method: "PUT", headers: { ...hdrs, "Content-Type": "application/json" },
        body: JSON.stringify({ porcentaje_comision: pct }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error al guardar");
      toast.success("Comisión actualizada");
      setEditandoId(null);
      cargar();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="space-y-4 py-4">
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3">
        <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-300 space-y-1">
          <p>El vendedor de cada venta es <strong className="text-white">el usuario que la registra</strong> — ya no hace falta elegirlo a mano. Acá se configura el % de comisión de cada usuario de esta empresa.</p>
          <p>También cobran comisión los <strong className="text-white">vendedores del Catastro</strong> (entidades con rol Vendedor, internos o externos) elegidos en la venta, con el % definido en su ficha.</p>
          <p>La comisión se calcula sobre el total de venta, descontando primero las Notas de Crédito vinculadas a esa factura.</p>
          <p>Para dar de alta o baja usuarios, o para permitir que alguien corrija el vendedor de una factura ya cargada, andá a <strong className="text-white">Usuarios</strong>.</p>
        </div>
      </div>

      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-white">Usuarios y su % de comisión</span>
          <span className="text-xs text-slate-500">({usuarios.length})</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm">Cargando...</div>
        ) : usuarios.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
            No hay usuarios asignados a esta empresa todavía. Creálos en Usuarios.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {usuarios.map(u => (
              <div key={u.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-white/3 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-300 text-sm font-bold">
                    {(u.nombre || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{u.nombre}</p>
                    {u.email && <p className="text-slate-500 text-xs">{u.email}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {editandoId === u.id ? (
                    <>
                      <input type="number" min="0" max="100" step="0.1" value={pctEdit} autoFocus
                        onChange={e => setPctEdit(e.target.value)}
                        className="w-20 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white text-sm focus:outline-none focus:border-blue-500" />
                      <span className="text-slate-400 text-sm">%</span>
                      <button onClick={() => guardar(u.id)} disabled={saving}
                        className="h-7 px-2.5 text-xs bg-blue-600/80 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50">
                        {saving ? "..." : "Guardar"}
                      </button>
                      <button onClick={() => setEditandoId(null)}
                        className="h-7 px-2.5 text-xs bg-white/5 hover:bg-white/10 text-slate-400 rounded-lg">
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-amber-500/15 text-amber-300 border-amber-500/30">
                        <Percent className="w-3 h-3" /> {Number(u.porcentaje_comision || 0).toFixed(1)}%
                      </span>
                      <button onClick={() => abrirEditar(u)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-white">Vendedores (Catastro) y su % de comisión</span>
          <span className="text-xs text-slate-500">({vendedoresEnt.length})</span>
        </div>
        {vendedoresEnt.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-slate-500 text-sm px-4 text-center">
            No hay entidades con rol Vendedor. Marcá "Vendedor" y cargá el % de comisión en Catastro.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {vendedoresEnt.map(v => (
              <div key={v.id} className="flex items-center justify-between px-5 py-3">
                <p className="text-white text-sm">{v.nombre}</p>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-amber-500/15 text-amber-300 border-amber-500/30">
                  <Percent className="w-3 h-3" /> {Number(v.porcentaje_comision || 0).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="px-5 py-2 text-[11px] text-slate-500 border-t border-white/5">El % de cada vendedor se edita en su ficha del Catastro.</p>
      </div>

      <ComisionesConfigPanel />
    </div>
  );
}
